/**
 * Vouchers Service
 * Handles voucher management for admin/staff users
 */

import { supabase } from "./supabase";
import type { Voucher, VoucherFilters } from "../types/voucher.types";

/**
 * Get all vouchers with optional filtering
 */
export async function getVouchers(filters?: VoucherFilters) {
  let query = supabase
    .from("vouchers")
    .select("*")
    .order("created_at", { ascending: false });

  // Apply status filter
  if (filters?.status) {
    const now = Date.now();

    switch (filters.status) {
      case "redeemed":
        query = query.eq("is_redeemed", true);
        break;
      case "expired":
        query = query.eq("is_redeemed", false).lt("end_date", now);
        break;
      case "active":
        query = query
          .eq("is_redeemed", false)
          .or(`end_date.is.null,end_date.gt.${now}`);
        break;
    }
  }

  // Apply search filter
  if (filters?.search) {
    query = query.or(
      `voucher_code.ilike.*${filters.search}*,email.ilike.*${filters.search}*,voucher_name.ilike.*${filters.search}*`,
    );
  }

  // Apply date range filter
  if (filters?.dateFrom) {
    query = query.gte("created_at", filters.dateFrom);
  }
  if (filters?.dateTo) {
    query = query.lte("created_at", filters.dateTo);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching vouchers:", error);
    throw error;
  }

  return data as Voucher[];
}

/**
 * Get voucher analytics
 */
export async function getVoucherAnalytics() {
  const { data: vouchers, error } = await supabase.from("vouchers").select("*");

  if (error) {
    console.error("Error fetching voucher analytics:", error);
    throw error;
  }

  const now = Date.now();
  const totalGenerated = vouchers.length;
  const totalRedeemed = vouchers.filter((v) => v.is_redeemed).length;
  const redemptionRate =
    totalGenerated > 0 ? (totalRedeemed / totalGenerated) * 100 : 0;

  // Calculate total values
  const totalValueIssued = vouchers.reduce(
    (sum, v) => sum + (v.discount_value || 0),
    0,
  );
  const totalValueRedeemed = vouchers
    .filter((v) => v.is_redeemed)
    .reduce((sum, v) => sum + (v.discount_value || 0), 0);

  // Calculate time-based counts
  const oneDayAgo = now - 24 * 60 * 60 * 1000;
  const oneWeekAgo = now - 7 * 24 * 60 * 60 * 1000;
  const oneMonthAgo = now - 30 * 24 * 60 * 60 * 1000;

  const redemptionsToday = vouchers.filter(
    (v) =>
      v.is_redeemed &&
      v.redeemed_at &&
      new Date(v.redeemed_at).getTime() >= oneDayAgo,
  ).length;

  const redemptionsThisWeek = vouchers.filter(
    (v) =>
      v.is_redeemed &&
      v.redeemed_at &&
      new Date(v.redeemed_at).getTime() >= oneWeekAgo,
  ).length;

  const redemptionsThisMonth = vouchers.filter(
    (v) =>
      v.is_redeemed &&
      v.redeemed_at &&
      new Date(v.redeemed_at).getTime() >= oneMonthAgo,
  ).length;

  return {
    totalGenerated,
    totalRedeemed,
    redemptionRate,
    totalValueIssued,
    totalValueRedeemed,
    redemptionsToday,
    redemptionsThisWeek,
    redemptionsThisMonth,
  };
}

/**
 * Export vouchers to CSV
 */
export function exportVouchersToCSV(vouchers: Voucher[]) {
  const headers = [
    "Code",
    "Email",
    "Voucher Name",
    "Discount Value",
    "Status",
    "Created Date",
    "Redeemed Date",
    "Redeemed By",
  ];

  const rows = vouchers.map((v) => [
    v.voucher_code,
    v.email,
    v.voucher_name,
    v.discount_value.toString(),
    v.is_redeemed ? "Redeemed" : "Active",
    new Date(v.created_at).toLocaleDateString(),
    v.redeemed_at ? new Date(v.redeemed_at).toLocaleDateString() : "",
    v.redeemed_by || "",
  ]);

  const csvContent = [
    headers.join(","),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);

  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `vouchers-${new Date().toISOString().split("T")[0]}.csv`,
  );
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Cancel a voucher
 */
export async function cancelVoucher(voucherId: UUID) {
  const { data, error } = await supabase
    .from("vouchers")
    .update({
      status: "cancelled",
      is_cancelled: true,
      cancelled_at: new Date().toISOString(),
    })
    .eq("id", voucherId)
    .select()
    .single();

  if (error) {
    console.error("Error cancelling voucher:", error);
    throw error;
  }

  return data;
}

/**
 * Mark voucher as test (for production testing)
 * Test vouchers are excluded from partner reporting and analytics
 */
export async function testVoucher(voucherId: UUID) {
  const { data, error } = await supabase
    .from("vouchers")
    .update({
      status: "test",
      cancelled_at: new Date().toISOString(), // Use cancelled_at field to track when marked as test
    })
    .eq("id", voucherId)
    .select()
    .single();

  if (error) {
    console.error("Error marking voucher as test:", error);
    throw error;
  }

  return data;
}

export interface VendorPaidResult {
  voucher_id: UUID;
  vendor_paid: boolean;
  vendor_paid_at: string | null;
  vendor_paid_by: UUID | null;
}

export async function markVoucherVendorPaid(
  voucherId: UUID,
  vendorPaid: boolean,
): Promise<VendorPaidResult> {
  const { data, error } = await supabase.rpc("mark_voucher_vendor_paid", {
    p_voucher_id: voucherId,
    p_vendor_paid: vendorPaid,
  });

  if (error) {
    console.error("Error marking voucher vendor_paid:", error);
    throw error;
  }

  return data as VendorPaidResult;
}
